import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaSearch,
  FaPlus,
  FaTimes,
  FaHistory,
  FaDownload,
  FaTshirt,
  FaHardHat,
  FaMobileAlt,
  FaCar,
  FaTools,
  FaClipboardList,
  FaUndo,
  FaUsers,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import InventoryUpdate from '../../ResourceUpdate/components/InventoryUpdate';
import { useAdminPageSearch } from '../../../utils/useAdminPageSearch';
import '../styles/UniformEquipmentDashboard.css';

const FIND_ALL_ITEMS = `
  query FindAllItems {
    findAllItem {
      id
      name
      code
      quantity
      available_quantity
      description
    }
  }
`;

const FIND_ALL_PROJECTS = `
  query FindAllProjects {
    findAllProject {
      id
      name
      booking {
        id
        fullName
        address
      }
    }
  }
`;

const FIND_ALL_INVENTORY_MANAGEMENT = `
  query FindAllInventoryManagement {
    findAllInventoryManagement {
      id
      tool {
        id
        name
        code
        quantity
        available_quantity
      }
      project {
        id
        name
        booking {
          id
          fullName
          address
        }
      }
      quantityAssigned
      assignedAt
      assignedBy
      requestedBy
      remark
      updateAction
      category
      status
    }
  }
`;

const USERS_QUERY = `
  query UniformEquipmentUsers($page: Int, $limit: Int) {
    getAllUser(page: $page, limit: $limit) {
      data {
        id
        firstName
        lastName
        email
        roles
        employeeCode
        jobTitle
      }
      totalPages
    }
  }
`;

const MARK_RETURNED = `
  mutation MarkInventoryReturned($updateInventoryManagementInput: UpdateInventoryManagementInput!) {
    updateInventoryManagement(updateInventoryManagementInput: $updateInventoryManagementInput) {
      id
      updateAction
      remark
      assignedBy
    }
  }
`;

/** Primary workforce views (sir’s brief). */
const VIEW_TABS = [
  {
    key: 'Equipment',
    label: 'Equipment',
    icon: FaTools,
    blurb: 'All cleaning equipment in office inventory',
  },
  {
    key: 'Uniform',
    label: 'Uniform',
    icon: FaTshirt,
    blurb: 'Uniforms issued to staff — assigned date & yearly refresh',
  },
  {
    key: 'PPE',
    label: 'PPE',
    icon: FaHardHat,
    blurb: 'Personal protective equipment stock and issues',
  },
  {
    key: 'Assigned',
    label: 'Assigned',
    icon: FaClipboardList,
    blurb: 'Everything currently out with staff',
  },
  {
    key: 'Device',
    label: 'Device',
    icon: FaMobileAlt,
    blurb: 'Office devices, holders, and returns',
  },
  {
    key: 'Vehicle',
    label: 'Vehicle',
    icon: FaCar,
    blurb: 'Fleet vehicles, driver, plate/number, destination',
  },
];

function formatDate(dateLike) {
  const d = dateLike ? new Date(dateLike) : null;
  if (!d || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function normLower(s) {
  return String(s || '').toLowerCase();
}

function isReturnAction(action) {
  return normLower(action).includes('return');
}

function isIssueLike(rec) {
  if (isReturnAction(rec?.updateAction)) return false;
  const action = normLower(rec?.updateAction);
  if (action.includes('add to stock')) return false;
  if (String(rec?.status || '').toUpperCase() === 'REJECTED') return false;
  if (String(rec?.status || '').toUpperCase() === 'PENDING') return false;
  return true;
}

function isCurrentlyOut(rec) {
  return isIssueLike(rec) && !isReturnAction(rec?.updateAction);
}

function deriveCondition(rec) {
  const combo = `${normLower(rec?.updateAction)} ${normLower(rec?.remark)}`;
  if (combo.includes('damaged') || combo.includes('damage')) {
    return { label: 'Damaged', tone: 'warn' };
  }
  if (combo.includes('lost') || combo.includes('missing')) {
    return { label: 'Missing', tone: 'danger' };
  }
  if (combo.includes('worn')) return { label: 'Worn', tone: 'warn' };
  if (isReturnAction(rec?.updateAction)) return { label: 'Returned', tone: 'muted' };
  return { label: 'Good', tone: 'good' };
}

function deriveMovement(rec) {
  if (isReturnAction(rec?.updateAction)) {
    return { label: 'Returned', tone: 'muted' };
  }
  const combo = `${normLower(rec?.updateAction)} ${normLower(rec?.remark)}`;
  if (combo.includes('lost') || combo.includes('missing')) {
    return { label: 'Lost', tone: 'danger' };
  }
  if (combo.includes('damaged') || combo.includes('damage')) {
    return { label: 'Damaged', tone: 'warn' };
  }
  if (combo.includes('dispose') || combo.includes('used')) {
    return { label: 'Used', tone: 'muted' };
  }
  return { label: 'Issued', tone: 'issued' };
}

function inferCategoryFromName(name, code) {
  const hay = `${name || ''} ${code || ''}`.toLowerCase();
  if (/shirt|pants|trouser|boot|uniform|jacket|apron|polo|hoodie/.test(hay)) {
    return 'Uniform';
  }
  if (/helmet|glove|goggle|ppe|mask|vest|safety|ear.?muff|respirator/.test(hay)) {
    return 'PPE';
  }
  if (/phone|ipad|tablet|laptop|device|radio|scanner|gps/.test(hay)) {
    return 'Device';
  }
  if (/van|vehicle|car|truck|ute|fleet|rego/.test(hay)) return 'Vehicle';
  if (/key|access|fob|card|lock/.test(hay)) return 'Access';
  return 'Equipment';
}

function inferCategory(rec) {
  const explicit = String(rec?.category || '').trim();
  const known = [
    'Uniform',
    'PPE',
    'Access',
    'Device',
    'Vehicle',
    'Equipment',
  ];
  const hit = known.find((k) => k.toLowerCase() === explicit.toLowerCase());
  if (hit) return hit;
  return inferCategoryFromName(rec?.tool?.name, rec?.tool?.code);
}

function holderName(rec) {
  return (
    String(rec?.assignedBy || '').trim() ||
    String(rec?.requestedBy || '').trim() ||
    '—'
  );
}

function destinationOf(rec) {
  const project = rec?.project?.name || '';
  const client = rec?.project?.booking?.fullName || '';
  const address = rec?.project?.booking?.address || '';
  const parts = [project, client, address].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

function vehicleNumber(rec, item) {
  const fromRemark = String(rec?.remark || '').match(
    /(?:rego|plate|number|fleet)\s*[:#-]?\s*([A-Z0-9-]+)/i,
  );
  if (fromRemark?.[1]) return fromRemark[1].toUpperCase();
  return item?.code || rec?.tool?.code || '—';
}

/** Next yearly uniform refresh after last issue. */
function nextAnnualUpdate(assignedAt) {
  const start = assignedAt ? new Date(assignedAt) : null;
  if (!start || Number.isNaN(start.getTime())) return null;
  const next = new Date(start);
  next.setFullYear(next.getFullYear() + 1);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  while (next < today) next.setFullYear(next.getFullYear() + 1);
  return next;
}

function daysUntil(date) {
  if (!date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return Math.round((d - today) / (1000 * 60 * 60 * 24));
}

function buildExportCsv(rows) {
  const cols = [
    'Date',
    'Category',
    'Item',
    'Code',
    'Quantity',
    'Condition',
    'Movement',
    'Assigned To',
    'Project / Destination',
    'Update Action',
    'Remark',
  ];
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [cols.join(',')];
  for (const r of rows) {
    const cond = deriveCondition(r);
    const move = deriveMovement(r);
    lines.push(
      [
        formatDate(r.assignedAt),
        inferCategory(r),
        r.tool?.name || '—',
        r.tool?.code || '—',
        r.quantityAssigned ?? 0,
        cond.label,
        move.label,
        holderName(r),
        destinationOf(r),
        r.updateAction || '—',
        r.remark || '—',
      ]
        .map(escape)
        .join(','),
    );
  }
  return lines.join('\n');
}

function HistoryModal({ open, onClose, title, records }) {
  if (!open) return null;
  return (
    <div className="uqd-overlay" role="dialog" aria-modal="true">
      <div className="uqd-modal">
        <div className="uqd-modal-head">
          <h3>{title}</h3>
          <button type="button" className="uqd-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        <div className="uqd-history-list">
          {records.length === 0 ? (
            <div className="uqd-muted">No history found.</div>
          ) : (
            <ul>
              {records
                .slice()
                .sort(
                  (a, b) =>
                    new Date(b.assignedAt).getTime() -
                    new Date(a.assignedAt).getTime(),
                )
                .map((r) => {
                  const cond = deriveCondition(r);
                  const move = deriveMovement(r);
                  return (
                    <li key={r.id} className="uqd-history-row">
                      <div>
                        <strong>{formatDate(r.assignedAt)}</strong>
                        <div className="uqd-history-meta">
                          <span className={`uqd-badge uqd-badge--${cond.tone}`}>
                            {cond.label}
                          </span>
                          <span className={`uqd-move uqd-move--${move.tone}`}>
                            {move.label}
                          </span>
                          <span className="uqd-history-chip">
                            {inferCategory(r)}
                          </span>
                        </div>
                      </div>
                      <div className="uqd-history-right">
                        <div className="uqd-history-sub">
                          Assigned to: {holderName(r)}
                        </div>
                        <div className="uqd-history-sub">
                          {destinationOf(r)}
                        </div>
                        <div className="uqd-history-sub">
                          {r.updateAction || '—'}
                        </div>
                        {r.remark ? (
                          <div className="uqd-history-sub">{r.remark}</div>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default function UniformEquipmentDashboard() {
  const { get, patch } = useAdminPageSearch();
  const [loading, setLoading] = useState(true);
  const [inventoryMgmt, setInventoryMgmt] = useState([]);
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [staff, setStaff] = useState([]);

  const [search, setSearch] = useState('');
  const tabKeys = VIEW_TABS.map((t) => t.key);
  const urlTab = get('tab');
  const activeTab = tabKeys.includes(urlTab) ? urlTab : 'Equipment';
  const showIssueModal = get('action') === 'add';
  const [issueDefaults, setIssueDefaults] = useState({
    category: 'Equipment',
    updateAction: 'Issued to Staff',
  });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTitle, setHistoryTitle] = useState('');
  const [historyRecords, setHistoryRecords] = useState([]);
  const [returningId, setReturningId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [itemsRes, projectsRes, mgmtRes, usersRes] = await Promise.all([
        graphqlRequest(FIND_ALL_ITEMS),
        graphqlRequest(FIND_ALL_PROJECTS),
        graphqlRequest(FIND_ALL_INVENTORY_MANAGEMENT),
        graphqlRequest(USERS_QUERY, { page: 1, limit: 200 }).catch(() => null),
      ]);
      setItems(itemsRes?.findAllItem || []);
      setProjects(projectsRes?.findAllProject || []);
      setInventoryMgmt(mgmtRes?.findAllInventoryManagement || []);
      setStaff(usersRes?.getAllUser?.data || []);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Failed to load uniform / equipment');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Latest category seen per inventory item id. */
  const categoryByItemId = useMemo(() => {
    const map = new Map();
    const sorted = [...(inventoryMgmt || [])].sort(
      (a, b) =>
        new Date(b.assignedAt).getTime() - new Date(a.assignedAt).getTime(),
    );
    sorted.forEach((r) => {
      const id = r.tool?.id;
      if (!id || map.has(id)) return;
      map.set(id, inferCategory(r));
    });
    return map;
  }, [inventoryMgmt]);

  const classifyStockItem = useCallback(
    (item) =>
      categoryByItemId.get(item.id) ||
      inferCategoryFromName(item.name, item.code),
    [categoryByItemId],
  );

  const stockByCategory = useMemo(() => {
    const groups = {
      Equipment: [],
      Uniform: [],
      PPE: [],
      Device: [],
      Vehicle: [],
      Access: [],
    };
    (items || []).forEach((item) => {
      const cat = classifyStockItem(item);
      if (!groups[cat]) groups[cat] = [];
      const outQty = Math.max(
        0,
        Number(item.quantity || 0) - Number(item.available_quantity || 0),
      );
      groups[cat].push({
        ...item,
        category: cat,
        outQty,
        onHand: Number(item.available_quantity || 0),
        total: Number(item.quantity || 0),
      });
    });
    Object.keys(groups).forEach((k) => {
      groups[k].sort((a, b) =>
        String(a.name).localeCompare(String(b.name)),
      );
    });
    return groups;
  }, [items, classifyStockItem]);

  const recordsEnriched = useMemo(
    () =>
      (inventoryMgmt || []).map((r) => ({
        ...r,
        category: inferCategory(r),
        condition: deriveCondition(r),
        movement: deriveMovement(r),
        holder: holderName(r),
        destination: destinationOf(r),
        out: isCurrentlyOut(r),
      })),
    [inventoryMgmt],
  );

  const matchesSearch = useCallback(
    (parts) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return parts
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q);
    },
    [search],
  );

  const activeRecords = useMemo(() => {
    return recordsEnriched.filter((r) => {
      if (activeTab === 'Assigned') return r.out;
      if (activeTab === 'Equipment') return false; // stock view
      if (['Uniform', 'PPE', 'Device', 'Vehicle'].includes(activeTab)) {
        if (r.category !== activeTab) return false;
      }
      return matchesSearch([
        r.tool?.name,
        r.tool?.code,
        r.holder,
        r.destination,
        r.category,
        r.updateAction,
        r.remark,
      ]);
    });
  }, [recordsEnriched, activeTab, matchesSearch]);

  const activeStock = useMemo(() => {
    if (!['Equipment', 'PPE', 'Device', 'Vehicle', 'Uniform'].includes(activeTab)) {
      return [];
    }
    // Uniform/PPE focus on people issues; still show stock strip for PPE/Equipment/Device/Vehicle
    if (activeTab === 'Uniform') return [];
    const list = stockByCategory[activeTab] || [];
    return list.filter((item) =>
      matchesSearch([item.name, item.code, item.description, item.category]),
    );
  }, [activeTab, stockByCategory, matchesSearch]);

  /** Uniform: group active issues by person. */
  const uniformByPerson = useMemo(() => {
    if (activeTab !== 'Uniform') return [];
    const map = new Map();
    recordsEnriched
      .filter((r) => r.category === 'Uniform' && r.out)
      .filter((r) =>
        matchesSearch([
          r.tool?.name,
          r.tool?.code,
          r.holder,
          r.destination,
        ]),
      )
      .forEach((r) => {
        const key = r.holder || 'Unassigned';
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(r);
      });
    return Array.from(map.entries())
      .map(([person, rows]) => ({
        person,
        rows: rows.sort(
          (a, b) =>
            new Date(b.assignedAt).getTime() -
            new Date(a.assignedAt).getTime(),
        ),
      }))
      .sort((a, b) => a.person.localeCompare(b.person));
  }, [activeTab, recordsEnriched, matchesSearch]);

  const summary = useMemo(() => {
    const out = recordsEnriched.filter((r) => r.out);
    const byCat = (cat) => out.filter((r) => r.category === cat).length;
    const equipStock = (stockByCategory.Equipment || []).length;
    const dueUniforms = out.filter((r) => {
      if (r.category !== 'Uniform') return false;
      const next = nextAnnualUpdate(r.assignedAt);
      const days = daysUntil(next);
      return days != null && days <= 45;
    }).length;
    return {
      equipmentInStock: equipStock,
      uniformsOut: byCat('Uniform'),
      uniformsDue: dueUniforms,
      ppeOut: byCat('PPE'),
      assignedOut: out.length,
      devicesOut: byCat('Device'),
      vehiclesOut: byCat('Vehicle'),
    };
  }, [recordsEnriched, stockByCategory]);

  const openIssue = useCallback(
    (category) => {
      const allowed = [
        'Uniform',
        'PPE',
        'Device',
        'Vehicle',
        'Equipment',
      ];
      const cat =
        category ||
        (allowed.includes(activeTab) ? activeTab : 'Equipment');
      setIssueDefaults({
        category: cat,
        updateAction: 'Issued to Staff',
      });
      patch({ action: 'add' });
    },
    [activeTab, patch],
  );

  const exportCsv = useCallback(() => {
    try {
      const rows =
        activeTab === 'Assigned'
          ? recordsEnriched.filter((r) => r.out)
          : activeTab === 'Equipment'
            ? []
            : recordsEnriched.filter((r) =>
                activeTab === 'Uniform' ||
                activeTab === 'PPE' ||
                activeTab === 'Device' ||
                activeTab === 'Vehicle'
                  ? r.category === activeTab
                  : true,
              );
      if (activeTab === 'Equipment') {
        const stockRows = (stockByCategory.Equipment || []).map((item) => ({
          assignedAt: new Date().toISOString(),
          category: 'Equipment',
          tool: item,
          quantityAssigned: item.onHand,
          assignedBy: 'In office stock',
          project: null,
          updateAction: 'Stock',
          remark: `Total ${item.total} · Out ${item.outQty}`,
        }));
        const csv = buildExportCsv(stockRows);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `equipment-stock-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return;
      }
      const csv = buildExportCsv(rows);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `uniform-equipment-${activeTab.toLowerCase()}-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.message || 'Export failed');
    }
  }, [activeTab, recordsEnriched, stockByCategory]);

  const openHistoryFor = useCallback(
    (rec) => {
      const code = rec?.tool?.code;
      const toolId = rec?.tool?.id;
      const history = (inventoryMgmt || []).filter(
        (r) =>
          (code && r.tool?.code === code) || (toolId && r.tool?.id === toolId),
      );
      setHistoryTitle(
        `History: ${rec?.tool?.name || 'Item'} (${code || toolId || ''})`,
      );
      setHistoryRecords(history);
      setHistoryOpen(true);
    },
    [inventoryMgmt],
  );

  const markReturned = useCallback(
    async (rec) => {
      if (!rec?.id) return;
      try {
        setReturningId(rec.id);
        const note = `Returned ${formatDate(new Date())}${
          rec.remark ? ` | ${rec.remark}` : ''
        }`;
        await graphqlRequest(MARK_RETURNED, {
          updateInventoryManagementInput: {
            id: rec.id,
            updateAction: 'Returned',
            remark: note,
            assignedBy: rec.assignedBy || undefined,
            category: rec.category || undefined,
          },
        });
        toast.success('Marked as returned — stock updated');
        await load();
      } catch (e) {
        toast.error(e?.message || 'Could not mark returned');
      } finally {
        setReturningId(null);
      }
    },
    [load],
  );

  const tabMeta = VIEW_TABS.find((t) => t.key === activeTab) || VIEW_TABS[0];
  const IssueIcon = tabMeta.icon;

  const issueButtonLabel = {
    Equipment: 'Issue equipment',
    Uniform: 'Issue uniform',
    PPE: 'Issue PPE',
    Assigned: 'Issue item',
    Device: 'Issue device',
    Vehicle: 'Assign vehicle',
  }[activeTab] || 'Issue item';

  return (
    <div className="uqd-page">
      <div className="uqd-top">
        <div>
          <h1>Uniform & Equipment</h1>
          <p>
            SAS office inventory and workforce issues — equipment on hand,
            uniforms &amp; PPE per person, devices, and fleet vehicles.
          </p>
        </div>
        <div className="uqd-actions">
          <button
            type="button"
            className="uqd-btn uqd-btn--ghost"
            onClick={exportCsv}
          >
            <FaDownload /> Export
          </button>
          <button
            type="button"
            className="uqd-btn uqd-btn--primary"
            onClick={() => openIssue()}
          >
            <FaPlus /> {issueButtonLabel}
          </button>
        </div>
      </div>

      <div className="uqd-summary">
        <div className="uqd-summary-card uqd-summary-card--total">
          <div className="uqd-summary-value">{summary.equipmentInStock}</div>
          <div className="uqd-summary-label">Equipment SKUs</div>
        </div>
        <div className="uqd-summary-card uqd-summary-card--good">
          <div className="uqd-summary-value">{summary.assignedOut}</div>
          <div className="uqd-summary-label">Currently assigned</div>
        </div>
        <div className="uqd-summary-card uqd-summary-card--warn">
          <div className="uqd-summary-value">{summary.uniformsDue}</div>
          <div className="uqd-summary-label">Uniforms due (≤45 days)</div>
        </div>
        <div className="uqd-summary-card uqd-summary-card--pink">
          <div className="uqd-summary-value">
            {summary.devicesOut + summary.vehiclesOut}
          </div>
          <div className="uqd-summary-label">Devices + vehicles out</div>
        </div>
      </div>

      <div className="uqd-howto">
        <strong>How this page works</strong>
        <ul>
          <li>
            <b>Equipment</b> lists stock from Resources → Products (office inventory).
          </li>
          <li>
            <b>Uniform / PPE / Device / Vehicle</b> use Inventory Update records
            (category + assigned person + project).
          </li>
          <li>
            <b>Assigned</b> is the live “who has what” list. Mark{' '}
            <b>Returned</b> to bring stock back.
          </li>
          <li>
            Uniforms show <b>yearly update</b> from the assigned date (+1 year).
          </li>
        </ul>
      </div>

      <div className="uqd-search-row">
        <div className="uqd-search">
          <FaSearch className="uqd-search-icon" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search item, code, person, project, destination…"
          />
        </div>
      </div>

      <div className="uqd-chip-row">
        {VIEW_TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              type="button"
              className={`uqd-chip ${activeTab === t.key ? 'is-active' : ''}`}
              onClick={() => patch({ tab: t.key })}
              title={t.blurb}
            >
              <Icon className="uqd-chip-icon" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="uqd-tab-intro">
        <IssueIcon />
        <div>
          <strong>{tabMeta.label}</strong>
          <span>{tabMeta.blurb}</span>
        </div>
      </div>

      {loading ? (
        <div className="uqd-empty">Loading inventory…</div>
      ) : (
        <>
          {/* —— EQUIPMENT: office stock —— */}
          {activeTab === 'Equipment' ? (
            activeStock.length === 0 ? (
              <div className="uqd-empty">
                No equipment found in inventory. Add items under Resources →
                Products, then issue them with category <b>Equipment</b>.
              </div>
            ) : (
              <div className="uqd-table-wrap">
                <table className="uqd-table">
                  <thead>
                    <tr>
                      <th>Equipment</th>
                      <th>Code</th>
                      <th>In office</th>
                      <th>Out</th>
                      <th>Total</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {activeStock.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className="uqd-cell-title">{item.name}</div>
                          {item.description ? (
                            <div className="uqd-cell-sub">{item.description}</div>
                          ) : null}
                        </td>
                        <td>{item.code || '—'}</td>
                        <td>
                          <span className="uqd-pill uqd-pill--good">
                            {item.onHand}
                          </span>
                        </td>
                        <td>{item.outQty}</td>
                        <td>{item.total}</td>
                        <td>
                          <button
                            type="button"
                            className="uqd-link-btn"
                            onClick={() => openIssue('Equipment')}
                          >
                            Issue
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : null}

          {/* —— UNIFORM: per employee / manager —— */}
          {activeTab === 'Uniform' ? (
            uniformByPerson.length === 0 ? (
              <div className="uqd-empty">
                No uniforms currently assigned. Click <b>Issue uniform</b> and
                choose the employee or manager.
              </div>
            ) : (
              <div className="uqd-person-grid">
                {uniformByPerson.map(({ person, rows }) => (
                  <section key={person} className="uqd-person-card">
                    <header className="uqd-person-head">
                      <div className="uqd-person-avatar">
                        <FaUsers />
                      </div>
                      <div>
                        <div className="uqd-person-name">{person}</div>
                        <div className="uqd-person-meta">
                          {rows.length} uniform item
                          {rows.length === 1 ? '' : 's'} issued
                        </div>
                      </div>
                    </header>
                    <ul className="uqd-person-list">
                      {rows.map((r) => {
                        const next = nextAnnualUpdate(r.assignedAt);
                        const days = daysUntil(next);
                        const dueSoon = days != null && days <= 45;
                        const overdue = days != null && days <= 0;
                        return (
                          <li key={r.id}>
                            <div>
                              <strong>
                                {r.tool?.name || '—'}
                                {r.quantityAssigned
                                  ? ` ×${r.quantityAssigned}`
                                  : ''}
                              </strong>
                              <div className="uqd-cell-sub">
                                {r.tool?.code || '—'} · Issued{' '}
                                {formatDate(r.assignedAt)}
                              </div>
                            </div>
                            <div className="uqd-person-right">
                              <span
                                className={`uqd-pill ${
                                  overdue
                                    ? 'uqd-pill--danger'
                                    : dueSoon
                                      ? 'uqd-pill--warn'
                                      : 'uqd-pill--good'
                                }`}
                              >
                                {overdue
                                  ? 'Update overdue'
                                  : `Update ${formatDate(next)}`}
                              </span>
                              <div className="uqd-row-actions">
                                <button
                                  type="button"
                                  className="uqd-link-btn"
                                  onClick={() => openHistoryFor(r)}
                                >
                                  <FaHistory /> History
                                </button>
                                <button
                                  type="button"
                                  className="uqd-link-btn"
                                  disabled={returningId === r.id}
                                  onClick={() => void markReturned(r)}
                                >
                                  <FaUndo /> Return
                                </button>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                ))}
              </div>
            )
          ) : null}

          {/* —— PPE / DEVICE / VEHICLE / ASSIGNED: table views —— */}
          {['PPE', 'Device', 'Vehicle', 'Assigned'].includes(activeTab) ? (
            <>
              {['PPE', 'Device', 'Vehicle'].includes(activeTab) &&
              activeStock.length > 0 ? (
                <div className="uqd-stock-strip">
                  <div className="uqd-stock-strip-title">
                    In office ({activeTab})
                  </div>
                  <div className="uqd-stock-chips">
                    {activeStock.map((item) => (
                      <div key={item.id} className="uqd-stock-chip">
                        <strong>{item.name}</strong>
                        <span>
                          {item.onHand} available · {item.code || 'no code'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {activeRecords.length === 0 ? (
                <div className="uqd-empty">
                  No {activeTab === 'Assigned' ? 'active assignments' : `${activeTab} records`}{' '}
                  found. Use <b>{issueButtonLabel}</b> to create one.
                </div>
              ) : (
                <div className="uqd-table-wrap">
                  <table className="uqd-table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        {activeTab === 'Assigned' ? <th>Type</th> : null}
                        {activeTab === 'Vehicle' ? <th>Number</th> : <th>Code</th>}
                        <th>Assigned to</th>
                        {activeTab === 'Vehicle' ? (
                          <th>Where going</th>
                        ) : (
                          <th>Project</th>
                        )}
                        <th>Issued</th>
                        {activeTab === 'Device' || activeTab === 'Vehicle' ? (
                          <th>Return</th>
                        ) : (
                          <th>Status</th>
                        )}
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {activeRecords
                        .slice()
                        .sort(
                          (a, b) =>
                            new Date(b.assignedAt).getTime() -
                            new Date(a.assignedAt).getTime(),
                        )
                        .map((r) => (
                          <tr key={r.id}>
                            <td>
                              <div className="uqd-cell-title">
                                {r.tool?.name || '—'}
                                {r.quantityAssigned
                                  ? ` ×${r.quantityAssigned}`
                                  : ''}
                              </div>
                            </td>
                            {activeTab === 'Assigned' ? (
                              <td>
                                <span className="uqd-history-chip">
                                  {r.category}
                                </span>
                              </td>
                            ) : null}
                            <td>
                              {activeTab === 'Vehicle'
                                ? vehicleNumber(r)
                                : r.tool?.code || '—'}
                            </td>
                            <td>{r.holder}</td>
                            <td>
                              <div className="uqd-cell-title">
                                {r.project?.name || '—'}
                              </div>
                              {activeTab === 'Vehicle' ? (
                                <div className="uqd-cell-sub">
                                  {r.project?.booking?.address ||
                                    r.project?.booking?.fullName ||
                                    ''}
                                </div>
                              ) : null}
                            </td>
                            <td>{formatDate(r.assignedAt)}</td>
                            <td>
                              {activeTab === 'Device' ||
                              activeTab === 'Vehicle' ? (
                                r.out ? (
                                  <span className="uqd-pill uqd-pill--warn">
                                    Out
                                  </span>
                                ) : (
                                  <span className="uqd-pill uqd-pill--muted">
                                    Returned
                                  </span>
                                )
                              ) : (
                                <span
                                  className={`uqd-move uqd-move--${r.movement.tone}`}
                                >
                                  {r.movement.label}
                                </span>
                              )}
                            </td>
                            <td>
                              <div className="uqd-row-actions">
                                <button
                                  type="button"
                                  className="uqd-link-btn"
                                  onClick={() => openHistoryFor(r)}
                                >
                                  <FaHistory />
                                </button>
                                {r.out ? (
                                  <button
                                    type="button"
                                    className="uqd-link-btn"
                                    disabled={returningId === r.id}
                                    onClick={() => void markReturned(r)}
                                    title="Mark returned"
                                  >
                                    <FaUndo />
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : null}
        </>
      )}

      {showIssueModal ? (
        <InventoryUpdate
          onClose={() => patch({}, ['action'])}
          onInventoryAdded={() => {
            patch({}, ['action']);
            void load();
          }}
          items={items}
          projects={projects}
          editData={null}
          defaultCategory={
            ['Uniform', 'PPE', 'Device', 'Vehicle', 'Equipment'].includes(
              activeTab,
            )
              ? activeTab
              : issueDefaults.category
          }
          defaultUpdateAction={issueDefaults.updateAction}
          staffOptions={staff}
          modalTitle={issueButtonLabel}
        />
      ) : null}

      <HistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={historyTitle}
        records={historyRecords}
      />
    </div>
  );
}
